declare global {
  interface Window {
    navigation: any;
  }
}

export type RouteParts = Record<string, string>;

export function onNavigate(handler: (parts: RouteParts) => void): () => void {
  const listener = (event) => {
    const url = new URL(event.destination.url);
    const parts = getRoutePartsFromUrl(url.search);
    handler(parts);
  };
  window.navigation.addEventListener('navigate', listener);
  return () => window.navigation.removeEventListener('navigate', listener);
}

export function getRouteParts(): RouteParts {
  return getRoutePartsFromUrl(location.search);
}

export function updateRouteParamsState(params: Record<string, string>): void {
  const url = getUrlWithUpdatedParams(params);
  history.replaceState(undefined, undefined, url);
}

export function setRouteParamsState(params: Record<string, string>): void {
  const url = getUrlWithParams(params);
  history.replaceState(undefined, undefined, url);
}

export function updateRouteParams(params: Record<string, string>): void {
  location.replace(getUrlWithUpdatedParams(params));
}

export function getQueryParam(param: string): string | undefined {
  const { [param]: [value] = [] } = getQueryParams(location.search);
  return value;
}

export function getValuesFromUrlQuery(): Record<string, any> {
  const params = getQueryParams(location.search);
  return Object.entries(params).reduce((acc, [key, value]) => ({
    ...acc,
    [key]: value.join('\n'),
  }), {});
}

function getUrlWithParams(params: Record<string, string>): string {
  const { protocol, host, pathname } = location;
  const query = new URLSearchParams(params);
  return `${protocol}//${host}${pathname}?${query}`;
}

function getUrlWithUpdatedParams(updates: Record<string, string>): string {
  const { protocol, host, pathname, search } = location;
  const params = new URLSearchParams(search);
  Object.entries(updates).forEach(([key, value]) => {
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
  });
  return `${protocol}//${host}${pathname}?${params}`;
}

function getRoutePartsFromUrl(url: string): RouteParts {
  const {
          login: [login]       = [],
          form: [form]         = [],
          preview: [preview]   = [],
          field: [field]       = [],
          group: [group]       = [],
          edit: [edit]         = [],
          design: [design]     = [],
          settings: [settings] = [],
        } = getQueryParams(url);
  return { login, settings, form, preview, edit, design, field, group };
}

function getQueryParams(url: string): Record<string, string[]> {
  const params: Record<string, string[]> = {};
  const search = new URLSearchParams(url);
  for (const [key, value] of search) {
    params[key] = [...(params[key] ?? []), value];
  }
  return params;
}

export function routeTo(parts: Partial<RouteParts> = {}) {
  return () => {
    setRouteParamsState(parts);
  };
}

export function hrefTo(parts: Partial<RouteParts> = {}): string {
  return `?${String(new URLSearchParams(parts))}`;
}
